// Q9 S6 / D8 — Top-of-step summary banner.
//
// Spec (docs/specs/visit-report-per-section-validation.md §Server error
// → UI surfacing): "Anchor under the most-relevant field; secondary
// banner at top of the step ("Fix the highlighted field to continue")."
//
// Two failure surfaces this banner owns:
//   - fieldErrorCount > 0 : "N fields need attention" — the per-field
//     errors are anchored inline (FormField/SelectField/FieldError);
//     the banner gives the carer a count and a directive.
//   - formErrors          : section-level Zod errors that don't anchor
//     to a single field (e.g., "At least one medication entry required").
//
// Hidden when both are empty so the section's normal chrome stays clean.

import type { JSX } from "react";

interface Props {
  fieldErrorCount: number;
  formErrors?:     readonly string[];
}

export function SectionErrorBanner({ fieldErrorCount, formErrors }: Props): JSX.Element | null {
  const hasFormErrors = (formErrors?.length ?? 0) > 0;
  if (fieldErrorCount === 0 && !hasFormErrors) return null;

  return (
    <div
      role="alert"
      data-testid="section-error-banner"
      className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      {fieldErrorCount > 0 && (
        <p className="font-medium" data-testid="field-error-count">
          {fieldErrorCount === 1
            ? "1 field needs attention"
            : `${fieldErrorCount} fields need attention`}
        </p>
      )}
      {hasFormErrors && (
        <ul className={fieldErrorCount > 0 ? "mt-1 list-disc pl-5" : "list-disc pl-5"}>
          {(formErrors ?? []).map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
